import { httpClient } from './http-client';

export type AllocationDimension = 'asset_class' | 'region' | 'sector';

export const ALLOCATION_DIMENSIONS: { value: AllocationDimension; label: string }[] = [
  { value: 'asset_class', label: 'Asset class' },
  { value: 'region', label: 'Region' },
  { value: 'sector', label: 'Sector' },
];

/** Category suggestions per dimension. Free text is allowed everywhere —
 * these only seed the autocomplete so households converge on one spelling. */
export const CATEGORY_SUGGESTIONS: Record<AllocationDimension, string[]> = {
  asset_class: ['Equity', 'Fixed income', 'Cash', 'Real estate', 'Crypto', 'Commodities'],
  region: ['Canada', 'United States', 'International', 'Emerging markets', 'Global'],
  sector: [
    'Financials', 'Technology', 'Energy', 'Healthcare', 'Industrials',
    'Consumer', 'Utilities', 'Materials', 'Telecom', 'Diversified',
  ],
};

export interface AllocationTargetLine {
  category: string;
  target_pct: string;
}

export interface AllocationTarget {
  id: string;
  household_id: string;
  name: string;
  dimension: AllocationDimension;
  lines: AllocationTargetLine[];
  created_at: string;
  updated_at: string;
}

export interface AllocationTargetRequest {
  name: string;
  dimension: AllocationDimension;
  lines: AllocationTargetLine[];
}

export interface SymbolClassification {
  id: string;
  household_id: string;
  symbol: string;
  dimension: AllocationDimension;
  category: string;
  source: 'manual' | 'ai';
}

/** category: '' removes the classification for that (symbol, dimension). */
export interface ClassificationUpsert {
  symbol: string;
  dimension: AllocationDimension;
  category: string;
}

export interface DriftRow {
  category: string;
  actual_pct: string;
  target_pct: string;
  /** actual − target: positive = overweight. */
  drift_pct: string;
  actual_value: string;
}

export interface DriftResponse {
  target: AllocationTarget;
  drift: DriftRow[];
  analysis_type: string;
}

export interface TradeSuggestion {
  action: 'buy' | 'sell';
  category: string;
  amount: string;
  /** The category's holdings, largest first — candidates, not instructions. */
  symbols: string[];
}

export interface RebalanceResponse {
  target: AllocationTarget;
  trades: TradeSuggestion[];
  analysis_type: string;
}

export class AllocationTargetsService {
  private static readonly BASE_URL = '/allocation';

  static async listTargets(): Promise<AllocationTarget[]> {
    const raw = await httpClient.get<any>(`${AllocationTargetsService.BASE_URL}/targets`);
    return (raw as any).data || raw;
  }

  static async createTarget(data: AllocationTargetRequest): Promise<AllocationTarget> {
    const raw = await httpClient.post<any>(`${AllocationTargetsService.BASE_URL}/targets`, data);
    return (raw as any).data || raw;
  }

  static async updateTarget(id: string, data: AllocationTargetRequest): Promise<AllocationTarget> {
    const raw = await httpClient.put<any>(`${AllocationTargetsService.BASE_URL}/targets/${id}`, data);
    return (raw as any).data || raw;
  }

  static async deleteTarget(id: string): Promise<void> {
    await httpClient.delete(`${AllocationTargetsService.BASE_URL}/targets/${id}`);
  }

  static async listClassifications(): Promise<SymbolClassification[]> {
    const raw = await httpClient.get<any>(`${AllocationTargetsService.BASE_URL}/classifications`);
    return (raw as any).data || raw;
  }

  static async putClassifications(updates: ClassificationUpsert[]): Promise<SymbolClassification[]> {
    const raw = await httpClient.put<any>(
      `${AllocationTargetsService.BASE_URL}/classifications`, updates,
    );
    return (raw as any).data || raw;
  }

  static async drift(targetId: string): Promise<DriftResponse> {
    const raw = await httpClient.get<any>(
      `${AllocationTargetsService.BASE_URL}/drift?target_id=${encodeURIComponent(targetId)}`,
    );
    return (raw as any).data || raw;
  }

  static async rebalance(
    targetId: string,
    opts: { minTrade?: string; buyOnly?: boolean; cash?: string } = {},
  ): Promise<RebalanceResponse> {
    const params = new URLSearchParams({ target_id: targetId });
    if (opts.minTrade) params.set('min_trade', opts.minTrade);
    if (opts.buyOnly) params.set('buy_only', 'true');
    if (opts.cash) params.set('cash', opts.cash);
    const raw = await httpClient.get<any>(
      `${AllocationTargetsService.BASE_URL}/rebalance?${params.toString()}`,
    );
    return (raw as any).data || raw;
  }
}
